import { PAST_DUE_GRACE_DAYS, PRODUCTS, type ProductId } from '@bb/shared';
import { createFileRoute, Link, Outlet, redirect, useRouterState } from '@tanstack/react-router';
import {
  CalendarCheck,
  CreditCard,
  FileSignature,
  Inbox,
  Menu,
  Plus,
  Receipt,
  Settings,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadCurrentUser, useCurrentUser, useLogout } from '@/features/auth/use-current-user';
import { daysUntil } from '@/features/billing/use-billing';
import { changeLocale, ensureNamespace } from '@/lib/i18n';
import { isSectionActive, PRODUCT_SECTIONS } from '@/lib/product-nav';
import { productPath } from '@/lib/routes';

export const Route = createFileRoute('/app')({
  beforeLoad: async ({ context, location }) => {
    const user = await loadCurrentUser(context.queryClient);
    if (!user) {
      throw redirect({ to: '/login', search: { returnTo: location.href } });
    }
    /*
      `auth` is in here because two screens inside the shell name a sign-in
      provider — Settings and Booking → Calendars — and neither route loaded it,
      so both rendered the raw key: "Signed in with providers.google". It is
      under a kilobyte, and loading it per route is how this came back once
      already.
    */
    await Promise.all([
      ensureNamespace('account'),
      ensureNamespace('billing'),
      ensureNamespace('auth'),
    ]);
    if (user.locale !== document.documentElement.lang) await changeLocale(user.locale);
    return { user };
  },
  component: AppShell,
});

const ICONS: Record<ProductId, typeof CalendarCheck> = {
  booking: CalendarCheck,
  agreements: FileSignature,
  invoicing: Receipt,
  inquiry: Inbox,
  clients: Users,
};

/**
 * The free plan never lapses, so the only thing worth interrupting anyone
 * about is a failed payment: paid features keep working through the grace
 * window, and silence until they stop is the worst version of a grace period.
 */
function BillingBanner(props: {
  status: string;
  currentPeriodEndsAt: string | null;
  hidden: boolean;
}) {
  const { t } = useTranslation('billing');
  if (props.hidden || props.status !== 'past_due') return null;
  const graceEndsAt = props.currentPeriodEndsAt
    ? new Date(new Date(props.currentPeriodEndsAt).getTime() + PAST_DUE_GRACE_DAYS * 86_400_000)
    : null;
  const message = t('pastDueBanner', { days: daysUntil(graceEndsAt?.toISOString() ?? null) });
  return (
    <div
      className={`mb-6 flex flex-col items-start gap-2 rounded-lg border px-4 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4 ${'border-amber-300 bg-amber-50 text-amber-900'}`}
    >
      <span>{message}</span>
      <Link to="/app/billing" className="shrink-0 font-medium underline">
        {t('subscribe')}
      </Link>
    </div>
  );
}

const navItem = (active: boolean) =>
  `flex min-h-11 items-center gap-2 rounded px-2 text-sm md:min-h-9 ${
    active ? 'bg-neutral-100 font-medium text-neutral-900' : 'text-neutral-600 hover:bg-neutral-50'
  }`;

/**
 * The whole navigation: products, the active product's sections, and the
 * account links. Rendered in the desktop sidebar and the mobile drawer, so
 * both always offer the same destinations.
 */
function SideNav(props: { pathname: string; onNavigate?: () => void }) {
  const { t } = useTranslation(['common', 'booking']);
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  if (!user) return null;

  const enabled = user.enabledProducts.map((id) => PRODUCTS[id]);
  const canAddProduct = Object.values(PRODUCTS).some(
    (p) => p.status === 'available' && !user.enabledProducts.includes(p.id),
  );

  return (
    <div className="flex h-full w-full flex-col">
      <Link
        to="/app"
        onClick={props.onNavigate}
        className="flex min-h-11 items-center px-4 pr-12 text-sm font-semibold md:min-h-0 md:py-4"
      >
        {t('common:app.name')}
      </Link>

      {/* Long product lists scroll here so the account links below stay put. */}
      <nav className="flex-1 overflow-y-auto px-2 pb-2">
        {enabled.map((product) => {
          const Icon = ICONS[product.id];
          const onProduct = props.pathname.startsWith(product.basePath);
          const sections = onProduct ? (PRODUCT_SECTIONS[product.id] ?? []) : [];
          return (
            <div key={product.id} className="mb-1">
              {/* While you are inside a product its name is a heading: the
                  first section link already points at the product root. */}
              {sections.length > 0 ? (
                <div className="flex min-h-11 items-center gap-2 px-2 text-sm font-medium text-neutral-900 md:min-h-9">
                  <Icon size={16} />
                  {t(`common:products.${product.id}.name`)}
                </div>
              ) : (
                <Link
                  to={productPath(product.id)}
                  onClick={props.onNavigate}
                  className={navItem(onProduct)}
                >
                  <Icon size={16} />
                  {t(`common:products.${product.id}.name`)}
                </Link>
              )}
              {sections.map((section) => (
                <Link
                  key={section.key}
                  to={section.to}
                  onClick={props.onNavigate}
                  className={`${navItem(isSectionActive(section, props.pathname))} pl-8`}
                >
                  {t(`booking:nav.${section.key}`)}
                </Link>
              ))}
            </div>
          );
        })}
        {canAddProduct ? (
          <Link
            to="/app/settings"
            onClick={props.onNavigate}
            className={`${navItem(false)} text-neutral-500`}
          >
            <Plus size={16} />
            {t('common:nav.addProduct')}
          </Link>
        ) : null}
      </nav>

      <div className="border-t border-neutral-200 px-2 py-2">
        <Link
          to="/app/billing"
          onClick={props.onNavigate}
          className={navItem(props.pathname.startsWith('/app/billing'))}
        >
          <CreditCard size={16} />
          {t('common:nav.billing')}
        </Link>
        <Link
          to="/app/settings"
          onClick={props.onNavigate}
          className={navItem(props.pathname.startsWith('/app/settings'))}
        >
          <Settings size={16} />
          {t('common:nav.settings')}
        </Link>
        <button
          type="button"
          onClick={() => logout.mutate()}
          className={`${navItem(false)} w-full text-left`}
        >
          {t('common:nav.signOut')}
        </button>
      </div>
    </div>
  );
}

/** Label for the mobile top bar, so the current page is named there too. */
function useCurrentTitle(pathname: string, enabledProducts: ProductId[]): string {
  const { t } = useTranslation(['common', 'booking']);
  if (pathname.startsWith('/app/billing')) return t('common:nav.billing');
  if (pathname.startsWith('/app/settings')) return t('common:nav.settings');
  for (const id of enabledProducts) {
    const product = PRODUCTS[id];
    if (!pathname.startsWith(product.basePath)) continue;
    const section = (PRODUCT_SECTIONS[id] ?? []).find((s) => isSectionActive(s, pathname));
    return section ? t(`booking:nav.${section.key}`) : t(`common:products.${id}.name`);
  }
  return t('common:app.name');
}

function AppShell() {
  const { t } = useTranslation('common');
  const { data: user } = useCurrentUser();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);
  const title = useCurrentTitle(pathname, user?.enabledProducts ?? []);

  // Close the drawer on navigation and lock the page behind it while it is open.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger, not a value the effect reads
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  if (!user) return null;

  return (
    <div className="flex min-h-full flex-col md:flex-row">
      <header className="sticky top-0 z-20 flex items-center gap-1 border-b border-neutral-200 bg-white px-2 py-2 md:hidden">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label={t('nav.menu')}
          aria-expanded={menuOpen}
          className="flex size-11 shrink-0 items-center justify-center rounded text-neutral-700 hover:bg-neutral-100"
        >
          <Menu size={20} />
        </button>
        <span className="truncate text-sm font-semibold">{title}</span>
      </header>

      {menuOpen ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label={t('actions.close')}
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-neutral-900/40"
          />
          <div className="relative flex h-full w-64 max-w-[85vw] flex-col bg-white shadow-xl">
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label={t('actions.close')}
              className="absolute top-2 right-2 flex size-11 items-center justify-center rounded text-neutral-500 hover:bg-neutral-100"
            >
              <X size={18} />
            </button>
            <SideNav pathname={pathname} onNavigate={() => setMenuOpen(false)} />
          </div>
        </div>
      ) : null}

      {/* Sticky so the account links at its foot never scroll away. */}
      <aside className="hidden w-56 shrink-0 border-r border-neutral-200 bg-white md:sticky md:top-0 md:block md:h-screen">
        <SideNav pathname={pathname} />
      </aside>

      <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <BillingBanner
          status={user.billing.status}
          currentPeriodEndsAt={user.billing.currentPeriodEndsAt}
          hidden={pathname.startsWith('/app/billing')}
        />
        <Outlet />
      </main>
    </div>
  );
}
